/* forager_side — the player walking to the right, eight frames. The engine
 * mirrors the set for left, so nothing in here may depend on which side of
 * the cell a thing sits on: every cue is drawn front-to-back along the walk,
 * not left-to-right on the screen.
 *
 * WHAT THE PREVIOUS EIGHT FRAMES ACTUALLY WERE. They passed spritecheck, and
 * they were still a bob. Measure the steps between consecutive frames and you
 * get 78/89/13/93/23/90/26/17: four transitions moved ~90 pixels and four
 * moved ~20. The ~90s are not steps. They are the WHOLE BODY translated down
 * one row and back — rows 1..13 were one rigid stamp, byte-identical in every
 * frame apart from that shift — and the ~20s are everything the legs did all
 * cycle. The spread check read 1.2x and passed because with exactly four big
 * and four small transitions the median lands *inside* the big group, so the
 * ratio it computes is 93/78 instead of 93/20. Four-four is the one shape
 * that check cannot see, and a 1px bob on 8 frames produces it every time.
 *
 * Two more things the old set got wrong, both consequences of that bob:
 *   - the cell is 16 rows and the body is 13 of them, so the legs live in
 *     rows 14-15 and nowhere else. Translating the body down a row pushed a
 *     leg row off the bottom of the cell: frames 1, 4 and 5 had ONE row of
 *     leg where the others had two. Leg mass ran 5, 6, 7, 9, 11, 11, 14, 14
 *     pixels — the boots halved and doubled every other frame.
 *   - the header claimed a treadmill ("lands at 8-11, walks back to 6-9, 4-7,
 *     2-4"). The pixels did not do that. The rear boot sat at columns 3, 3,
 *     4, 2, 4, 3 across the frames it was planted in: it jittered, it never
 *     travelled, so nothing was ever pushing the ground backward.
 *
 * SO: THE BODY DOES NOT TRANSLATE. At this size a one-row shift of a 13-row
 * body is 45+ pixels of change and the actual step is 12, so the bob does not
 * decorate the walk, it drowns it — and it costs half the legs to draw. Rows
 * 1..13 are anchored and the whole motion budget is spent below the hem. The
 * steps now read 35/34/19/21/37/33/21/22: uneven the way a gait is uneven
 * (the contacts move more than the passings) with nothing near a copy.
 *
 * THE TREADMILL, for real this time. One leg, eight phases, and the other leg
 * is the same table read four phases later:
 *
 *   phase   0     1     2     3     4  |  5     6     7
 *   col     9     8     6     4     2  |  4     7    10
 *   state  heel  flat  flat  flat  toe | air   air   air
 *
 * Stance is phases 0-4, five frames of eight, and the planted boot walks
 * backward through the cell the whole time — 9, 8, 6, 4, 2, never once
 * forward. Swing is three frames and covers the same ground the other way,
 * fastest in the middle (2 -> 4 -> 7 -> 10), overshooting to 10 at the reach
 * so the foot comes back one column onto its heel at 9. That last column is
 * the loop: phase 7's reaching boot at 10-13 with nothing under it is phase
 * 0's landed boot at 9-12 with a sole, and phase 7's planted far boot at 4-7
 * is at 2-5 by phase 0, which is the same 2-per-frame the treadmill has been
 * running all along. 7 into 0 costs 22 pixels — the median transition — so
 * there is no seam there to see.
 *
 * NEAR LEG AND FAR LEG ARE DIFFERENT COLOURS, and that is the only reason
 * this is eight frames rather than four played twice. A side-view walk is
 * symmetric: half a stride later the silhouette is identical, because both
 * legs are the same shape. The camera-side boot is drawn in the boot brown
 * `b`; the one on the far side of the body is drawn in `d`, a step darker, as
 * a limb behind a body is. That splits every pose into two — the wide contact
 * at 0 has the near foot landing and at 4 has the far foot landing, and the
 * two passing frames, which would otherwise be the same picture, now read as
 * "planted boot, sole lit" at 2 and "boot swinging past in front, the far
 * sole dark underneath it" at 6.
 *
 * THE HOOD SETTLES A ROW at 1 and 5, the recoils, one frame after each heel
 * strike. It does not translate the body: the crown drops onto the shoulder
 * line and the face and the hem do not move, so the feet stay planted and the
 * legs keep both their rows. That is 16 pixels, not 45.
 *
 * Hung off that, and deliberately NOT all on the same clock — anything that
 * runs on a period of four makes the two halves of the stride rhyme, which is
 * the thing this cycle is trying not to do:
 *   - the satchel rides its strap on the step (period four): down at 1 and 5,
 *     up at 3 and 7, at rest on the contacts. A bag on a strap lags the body;
 *     that one is allowed to rhyme, because it does.
 *   - the cloak's trailing edge (the column of outline behind the back) opens
 *     0,1,2,1,2,3,2,1 — twice a stride, but DEEPER on the half where the
 *     near leg drives back, because that is the leg on the cloak's side.
 *   - the scarf end is thrown back through the outline for two frames, 5 and
 *     6, by the near leg's push-off and by nothing else. It is the only teal
 *     that ever leaves the silhouette and it happens once per loop, so it
 *     also tells you which half of the stride you are in.
 *   - the front hem kicks out one pixel at 7 only, the frame where the near
 *     leg swings through it.
 * The face never moves. At sixteen pixels one thing has to be nailed down or
 * the whole sprite boils.
 *
 * Ink runs 142-150 of 256 across the eight; the old set ran 144-153 while
 * visibly clipping its own feet. Frame 0's body is `S.forager_side_0` row for
 * row, rows 0-13 unchanged. Its two leg rows are not: the shipped pose has
 * both boots flat with the REAR one's weight at the heel, which is a standing
 * pose, not a contact, and a rear foot about to push cannot be sitting on its
 * heel. Everything that makes the forager the forager — hood, face, scarf
 * band, belt, satchel, hem — is untouched.
 */

// phase 0 — CONTACT. The near boot lands at 9-12 on its heel: the sole is
// the two pixels at the BACK of it, the toe still up. The far boot at 2-5
// has rolled onto its toe and is one frame from leaving. Widest stance of
// the loop. Rows 0-13 are S.forager_side_0 verbatim.
export const forager_side_0 = [
  '................',
  '.....oooo.......',
  '....oCCCCo......',
  '..oCcccccccCo...',
  '..oCcccsssscc...',
  '..oCccsossscc...',
  '..oCcccsssscc...',
  '..oCkkkkkkkCo...',
  '..oCcccccccCo...',
  '..oCcccccccCoo..',
  '..oCcbbbbbcCoMo.',
  '..oCcccccccCoMo.',
  '..oCcccccccCo...',
  '..oocccccccco...',
  '..oddo...obbo...',
  '....dd....bb....',
];

// phase 1 — RECOIL. The hood settles a row onto the shoulders; the hem and
// both feet do not move, which is the whole point of doing the bob this way.
// The near boot rolls flat at 8-11 and takes the weight; the far one has
// broken contact and swings through at 4-7 with no sole under it. The
// satchel drops a row, lagging the landing the way a bag on a strap does.
export const forager_side_1 = [
  '................',
  '................',
  '.....oooo.......',
  '..oCoCCCCocCo...',
  '..oCcccsssscc...',
  '..oCccsossscc...',
  '..oCcccsssscc...',
  '..oCkkkkkkkCo...',
  '..oCcccccccCo...',
  '..oCcccccccCo...',
  '..oCcbbbbbcCoo..',
  '..oCcccccccCoMo.',
  '..oCcccccccCoMo.',
  '.ooocccccccco...',
  '....oddoobbo....',
  '.........bbb....',
];

// phase 2 — PASSING, near leg planted. The near boot is under the body at
// 6-9 carrying everything, and the far leg goes by BEHIND it — all that
// clears is the dark outline of its toe at column 10. One lit sole, dead
// centre. Compare phase 6.
export const forager_side_2 = [
  '................',
  '.....oooo.......',
  '....oCCCCo......',
  '..oCcccccccCo...',
  '..oCcccsssscc...',
  '..oCccsossscc...',
  '..oCcccsssscc...',
  '..oCkkkkkkkCo...',
  '..oCcccccccCo...',
  '..oCcccccccCoo..',
  '..oCcbbbbbcCoMo.',
  '..oCcccccccCoMo.',
  '.ooCcccccccCo...',
  '.ooocccccccco...',
  '......obboo.....',
  '.......bbb......',
];

// phase 3 — the near boot is late in its stance at 4-7 and still driving
// backward; the far leg is at full reach, 10-13, dark and off the ground.
// As wide as the stride gets before the far heel comes down. The satchel is
// at the top of its bounce.
export const forager_side_3 = [
  '................',
  '.....oooo.......',
  '....oCCCCo......',
  '..oCcccccccCo...',
  '..oCcccsssscc...',
  '..oCccsossscc...',
  '..oCcccsssscc...',
  '..oCkkkkkkkCo...',
  '..oCcccccccCoo..',
  '..oCcccccccCoMo.',
  '..oCcbbbbbcCoMo.',
  '..oCcccccccCo...',
  '..oCcccccccCo...',
  '.ooocccccccco...',
  '....obbo..oddo..',
  '.....bbb........',
];

// phase 4 — CONTACT, the other foot. The same stance as phase 0 with the
// legs swapped in depth: the FAR boot is the one landing, at 9-12, and the
// near boot at 2-5 is on its toe about to push. Depth is what tells the two
// contacts apart — that, and the cloak, which is two pixels open here and
// shut at phase 0.
export const forager_side_4 = [
  '................',
  '.....oooo.......',
  '....oCCCCo......',
  '..oCcccccccCo...',
  '..oCcccsssscc...',
  '..oCccsossscc...',
  '..oCcccsssscc...',
  '..oCkkkkkkkCo...',
  '..oCcccccccCo...',
  '..oCcccccccCoo..',
  '..oCcbbbbbcCoMo.',
  '..oCcccccccCoMo.',
  '.ooCcccccccCo...',
  '.ooocccccccco...',
  '..obbo...oddo...',
  '....bb....dd....',
];

// phase 5 — RECOIL, and the loud one. The hood settles again, the far boot
// rolls flat at 8-11, the near boot is up at 4-7. The near leg pushing off
// on the camera side throws the scarf end back out through the outline, and
// opens the cloak to its widest of the loop. Both of those happen once per
// stride, not twice, so they say which half you are watching.
export const forager_side_5 = [
  '................',
  '................',
  '.....oooo.......',
  '..oCoCCCCocCo...',
  '..oCcccsssscc...',
  '..oCccsossscc...',
  '..oCcccsssscc...',
  '.kkCkkkkkkkCo...',
  '..oCcccccccCo...',
  '..oCcccccccCo...',
  '..oCcbbbbbcCoo..',
  '.ooCcccccccCoMo.',
  '.ooCcccccccCoMo.',
  '.ooocccccccco...',
  '....obbooddo....',
  '.........ddd....',
];

// phase 6 — PASSING, near leg swinging, and the frame that proves this is
// eight frames and not four played twice. The near boot passes IN FRONT at
// 7-10 with nothing under it; the sole still on the ground at 7-9 is the far
// one, drawn dark. Phase 2 was a lit sole under a planted boot. Same
// columns, opposite reading.
export const forager_side_6 = [
  '................',
  '.....oooo.......',
  '....oCCCCo......',
  '..oCcccccccCo...',
  '..oCcccsssscc...',
  '..oCccsossscc...',
  '..oCcccsssscc...',
  '.kkCkkkkkkkCo...',
  '..oCcccccccCo...',
  '..oCcccccccCoo..',
  '..oCcbbbbbcCoMo.',
  '..oCcccccccCoMo.',
  '.ooCcccccccCo...',
  '.ooocccccccco...',
  '......oobbo.....',
  '.......ddd......',
];

// phase 7 — REACH, and the hand-off. The near boot is at 10-13 in the air at
// full extension; the far boot is planted at 4-7. One frame on, the near
// boot comes back a column onto its heel at 9-12 and the far boot has walked
// to 2-5 — which is phase 0, at the same two-columns-a-frame the treadmill
// has been running all cycle. The near leg swinging through kicks the front
// hem out a pixel; the scarf has settled back under the hood.
export const forager_side_7 = [
  '................',
  '.....oooo.......',
  '....oCCCCo......',
  '..oCcccccccCo...',
  '..oCcccsssscc...',
  '..oCccsossscc...',
  '..oCcccsssscc...',
  '..oCkkkkkkkCo...',
  '..oCcccccccCoo..',
  '..oCcccccccCoMo.',
  '..oCcbbbbbcCoMo.',
  '..oCcccccccCo...',
  '..oCcccccccCo...',
  '.oooccccccccoo..',
  '....oddo..obbo..',
  '.....ddd........',
];
